import { useState } from "react";
import type { PolarConfig, Question, Response } from "../../lib/types";
import { submitResponse } from "../../lib/api";

type Props = {
  question: Question;
  streamId?: string | null;
  onSubmitted: (row: Response) => void;
  onError: (message: string) => void;
};

export function PolarAnswer({ question, streamId, onSubmitted, onError }: Props) {
  const cfg = question.config as PolarConfig;
  const [side, setSide] = useState<"a" | "b" | null>(null);
  const [why, setWhy] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const whyRequired = cfg.why_required && side !== null;
  const canSubmit = side !== null && !submitting && (!whyRequired || why.trim().length > 0);

  async function handleSubmit() {
    if (side === null) return;
    const choiceKey = side === "a" ? cfg.side_a.key : cfg.side_b.key;
    setSubmitting(true);
    try {
      const row = await submitResponse({
        questionId: question.id,
        text: why.trim(),
        choiceKey,
        streamId,
      });
      onSubmitted(row);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Senden fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  }

  function tile(which: "a" | "b") {
    const sideCfg = which === "a" ? cfg.side_a : cfg.side_b;
    const selected = side === which;
    return (
      <button
        key={which}
        type="button"
        onClick={() => setSide(which)}
        className={[
          "flex-1 rounded-2xl border p-5 text-left transition",
          selected
            ? "border-rc-berry bg-rc-berry/5 shadow-berry"
            : "border-rc-ink/10 bg-rc-paper hover:border-rc-ink/30",
        ].join(" ")}
        aria-pressed={selected}
      >
        <div className="text-lg font-semibold text-rc-ink">{sideCfg.label}</div>
        {sideCfg.description && (
          <div className="mt-2 text-sm text-rc-ink/70">{sideCfg.description}</div>
        )}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row">
        {tile("a")}
        {tile("b")}
      </div>

      <label className="flex flex-col gap-2">
        <span className="text-sm font-medium text-rc-ink">
          {cfg.why_prompt}{whyRequired ? " *" : ""}
        </span>
        <textarea
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          rows={3}
          className="rounded-lg border border-rc-ink/10 bg-rc-paper p-3 text-rc-ink focus:outline-none focus:ring-2 focus:ring-rc-berry"
        />
      </label>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="rounded-xl bg-rc-berry px-6 py-3 text-rc-cream font-medium shadow-berry transition disabled:opacity-50 disabled:shadow-none"
      >
        {submitting ? "Wird gesendet…" : "Antwort senden"}
      </button>
    </div>
  );
}
